import React from 'react';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { useAccountApplicantsQuery } from '../../api/useAccountApplicantsQuery';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Grid } from '../../../../CommonComponents/OPL/Grid/Grid';
import { Row } from '../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../CommonComponents/OPL/Grid/Column';
import { Separator } from '../../../../CommonComponents/OPL/Separator/Separator';
import { getAccountTypeTitle } from './Summary.utils';
import { Stat } from '../../components/Stat/Stat';
import { ScrollableContent } from '../../MyAccount.styles';
import { MyAccountLoading } from '../../MyAccount.loading';
import { SummaryForm } from './components/SummaryForm/SummaryForm';
import { FORM_ID, FormValues } from './components/SummaryForm/SummaryForm.meta';
import { useUpdateAccountMutation } from '../../api/useUpdateAccountMutation';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';

interface Props {
  clientId: string;
  accountId: string;
  managedAccountId: string;
  onClose(): void;
  onSuccess(): void;
  setIsFormDirty(isDirty: boolean): void;
  onCloseAccount(): void;
}

export const Summary: React.FC<Props> = ({
  clientId,
  managedAccountId,
  accountId,
  onClose,
  onSuccess,
  setIsFormDirty,
  onCloseAccount,
}) => {
  const { isLoading, isError, refetch, data } = useAccountApplicantsQuery({
    clientId,
    managedAccountId,
  });

  const {
    mutateAsync: updateAccount,
    isLoading: isUpdatingAccount,
    error: updateAccountError,
  } = useUpdateAccountMutation();

  if (isLoading) {
    return <MyAccountLoading />;
  }

  if (isError) {
    return (
      <Box my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { primaryApplicant, secondApplicant, accountNumber, accountType, accountIraType } = data;

  const onSubmit = async (values: FormValues) => {
    try {
      await updateAccount({
        clientId,
        accountId,
        managedAccountId,
        accountInfo: {
          ...data,
          primaryApplicant: {
            ...data.primaryApplicant,
            ...values.primaryApplicant,
          },
          secondApplicant: data.secondApplicant
            ? {
                ...data.secondApplicant,
                ...values.secondApplicant,
              }
            : null,
        },
      });

      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <ScrollableContent>
        {updateAccountError && (
          <Alert message={updateAccountError.message} variant="error" mb="16" />
        )}

        <Box as="section">
          <Text as="h3" fontSize="h6" fontWeight={500} mb="16">
            Account Details
          </Text>

          <Grid>
            <Row mb="24">
              <Column width={6}>
                <Stat label="Name">
                  {primaryApplicant.firstName} {primaryApplicant.lastName}
                </Stat>
              </Column>

              {secondApplicant && (
                <Column width={6}>
                  <Stat label="Partner's name">
                    {secondApplicant.firstName} {secondApplicant.lastName}
                  </Stat>
                </Column>
              )}
            </Row>

            <Row>
              <Column width={6}>
                <Stat label="Account Type">{getAccountTypeTitle(accountType, accountIraType)}</Stat>
              </Column>

              <Column width={6}>
                <Stat label="Account number">{accountNumber}</Stat>
              </Column>
            </Row>
          </Grid>
        </Box>

        <Separator my="24" />

        <SummaryForm data={data} setIsFormDirty={setIsFormDirty} onSubmit={onSubmit} />
      </ScrollableContent>

      <ModalFooter as="footer" hasBorder>
        <Button variant="errorSecondary" onClick={onCloseAccount} mr="auto">
          Close My Account
        </Button>

        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button ml="8" type="submit" form={FORM_ID} isLoading={isUpdatingAccount}>
          Save
        </Button>
      </ModalFooter>
    </>
  );
};
